import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useCurrency } from "../lib/currency";
import api from "../lib/api";
import CurrencyToggle from "../components/CurrencyToggle";
import ProductForm from "../components/ProductForm";
import QuickSaleDialog from "../components/QuickSaleDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Sparkles, Plus, MoreVertical, Package, LogOut, Search, ShoppingBag, Pencil, Trash2, ExternalLink,
  AlertTriangle, PackageCheck, Zap, Settings as SettingsIcon, ClipboardList, Truck, History, TrendingUp, Wallet, Tag, Receipt, PackageX, Coins,
} from "lucide-react";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { format } = useCurrency();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [productFormOpen, setProductFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saleOpen, setSaleOpen] = useState(false);
  const [saleDefault, setSaleDefault] = useState({ productId: "", variantId: "" });
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products");
      setProducts(data || []);
    } catch (e) {
      toast.error("Erro ao carregar produtos");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user) load(); }, [user]);

  const filtered = useMemo(() => {
    if (!search) return products;
    return products.filter((p) => `${p.name} ${p.category} ${p.subcategory}`.toLowerCase().includes(search.toLowerCase()));
  }, [products, search]);

  const stats = useMemo(() => {
    let totalStockValue = 0, lowStock = 0, outOfStock = 0, totalUnits = 0;
    for (const p of products) {
      for (const v of p.variants || []) {
        totalStockValue += (Number(v.sale_price) || 0) * (Number(v.stock) || 0);
        totalUnits += Number(v.stock) || 0;
        if (v.stock === 0) outOfStock++;
        else if (v.stock <= 2) lowStock++;
      }
    }
    return { totalStockValue, lowStock, outOfStock, totalUnits, productCount: products.length };
  }, [products]);

  if (user === undefined) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">A carregar…</div>;
  if (user === null) return <Navigate to="/login" replace />;

  const handleEdit = (p) => { setEditingProduct(p); setProductFormOpen(true); };
  const handleNew = () => { setEditingProduct(null); setProductFormOpen(true); };
  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/products/${deleteId}`);
      toast.success("Produto eliminado");
      setDeleteId(null);
      load();
    } catch { toast.error("Erro ao eliminar"); }
  };
  const handleQuickSaleFor = (p, v) => {
    setSaleDefault({ productId: p.id, variantId: v.id });
    setSaleOpen(true);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      {/* Header */}
      <header className="sticky top-0 z-30 glass-nav border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/dashboard" className="flex items-center gap-2">
            <span className="font-display text-xl sm:text-2xl">Ladybeauty</span>
            <Sparkles className="w-4 h-4 text-[hsl(var(--accent))]" />
            <span className="hidden sm:inline text-xs text-muted-foreground ml-2">· Gestão</span>
          </Link>
          <div className="flex items-center gap-2">
            <CurrencyToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="user-menu-btn">
                  <MoreVertical className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem disabled className="text-xs">
                  {user.name} · {user.email}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/" className="cursor-pointer"><ExternalLink className="w-4 h-4 mr-2" /> Ver loja pública</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/dashboard/settings" className="cursor-pointer" data-testid="settings-menu-link"><SettingsIcon className="w-4 h-4 mr-2" /> Definições</Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { navigate("/"); logout(); }} className="cursor-pointer" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" /> Terminar sessão
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 pb-28 sm:pb-8 space-y-6">
        {/* Greeting */}
        <div className="lb-fade-in">
          <div className="lb-overline">Painel</div>
          <h1 className="font-display text-3xl sm:text-4xl mt-1">Olá, {user.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">Gere o teu stock e regista vendas em segundos.</p>
        </div>

        {/* Nav grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <NavCard to="/dashboard/vendas" icon={<History className="w-5 h-5" />} label="Vendas" testid="nav-vendas" />
          <NavCard to="/dashboard/fiados" icon={<Wallet className="w-5 h-5" />} label="Fiados" testid="nav-fiados" />
          <NavCard to="/dashboard/encomendas" icon={<Truck className="w-5 h-5" />} label="Encomendas" testid="nav-encomendas" />
          <NavCard to="/dashboard/lista-compras" icon={<ClipboardList className="w-5 h-5" />} label="Lista de compras" testid="nav-lista" />
          <NavCard to="/dashboard/despesas" icon={<Receipt className="w-5 h-5" />} label="Despesas" testid="nav-despesas" />
          <NavCard to="/dashboard/quebras" icon={<PackageX className="w-5 h-5" />} label="Quebras" testid="nav-quebras" />
          <NavCard to="/dashboard/categorias" icon={<Tag className="w-5 h-5" />} label="Categorias" testid="nav-categorias" />
          <NavCard to="/dashboard/financeiro" icon={<TrendingUp className="w-5 h-5" />} label="Financeiro" testid="nav-financeiro" />
          <NavCard to="/dashboard/divisao" icon={<Coins className="w-5 h-5" />} label="Divisão de dinheiro" testid="nav-divisao" />
          <NavCard to="/dashboard/settings" icon={<SettingsIcon className="w-5 h-5" />} label="Definições" testid="nav-settings" />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          <StatCard label="Produtos" value={stats.productCount} icon={<Package className="w-4 h-4" />} />
          <StatCard label="Unidades em stock" value={stats.totalUnits} icon={<PackageCheck className="w-4 h-4" />} />
          <StatCard label="Valor de stock" value={format(stats.totalStockValue)} icon={<ShoppingBag className="w-4 h-4" />} />
          <StatCard label="Stock baixo/esgotado" value={`${stats.lowStock} / ${stats.outOfStock}`} icon={<AlertTriangle className="w-4 h-4 text-[hsl(var(--destructive))]" />} highlight={stats.lowStock + stats.outOfStock > 0} />
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Pesquisar produtos..." className="pl-9 bg-white" data-testid="dashboard-search-input" />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleNew} variant="outline" data-testid="new-product-btn">
              <Plus className="w-4 h-4 mr-1" /> Novo produto
            </Button>
            <Button onClick={() => { setSaleDefault({ productId: "", variantId: "" }); setSaleOpen(true); }} className="bg-[hsl(var(--accent))] hover:bg-[#C48A1E] text-white" data-testid="quick-sale-btn">
              <Zap className="w-4 h-4 mr-1" /> Registar venda
            </Button>
          </div>
        </div>

        {/* Product list */}
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 bg-white rounded-2xl lb-shadow-card animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl lb-shadow-card" data-testid="empty-products">
            <Package className="w-12 h-12 mx-auto text-muted-foreground/50" />
            <h3 className="mt-3 font-display text-xl">Ainda não tens produtos</h3>
            <p className="text-sm text-muted-foreground mt-1">Adiciona o teu primeiro produto para começar.</p>
            <Button onClick={handleNew} className="mt-4 bg-[hsl(var(--primary))] hover:bg-[#A21F58]">
              <Plus className="w-4 h-4 mr-1" /> Adicionar produto
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((p) => <ProductRow key={p.id} product={p} onEdit={() => handleEdit(p)} onDelete={() => setDeleteId(p.id)} onSell={(v) => handleQuickSaleFor(p, v)} format={format} />)}
          </div>
        )}
      </main>

      {/* Mobile FAB for quick sale */}
      <button
        onClick={() => { setSaleDefault({ productId: "", variantId: "" }); setSaleOpen(true); }}
        className="sm:hidden fixed bottom-5 right-5 z-40 rounded-full bg-[hsl(var(--accent))] hover:bg-[#C48A1E] text-white h-14 w-14 shadow-lg flex items-center justify-center"
        data-testid="fab-quick-sale"
        aria-label="Registar venda"
      >
        <Zap className="w-6 h-6" />
      </button>

      <ProductForm open={productFormOpen} onOpenChange={setProductFormOpen} product={editingProduct} onSaved={load} />
      <QuickSaleDialog
        open={saleOpen}
        onOpenChange={setSaleOpen}
        products={products}
        defaultProductId={saleDefault.productId}
        defaultVariantId={saleDefault.variantId}
        onSaved={load}
      />

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar produto?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-[hsl(var(--destructive))] hover:bg-[#B03838]" data-testid="confirm-delete-btn">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatCard({ label, value, icon, highlight }) {
  return (
    <div className={`rounded-2xl p-4 bg-white lb-shadow-card ${highlight ? "ring-1 ring-[hsl(var(--destructive))]/30" : ""}`}>
      <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest">{icon}{label}</div>
      <div className="mt-1 font-display text-2xl">{value}</div>
    </div>
  );
}

function NavCard({ to, icon, label, testid }) {
  return (
    <Link
      to={to}
      className="bg-white rounded-2xl lb-shadow-card lb-hover-lift p-4 flex flex-col items-start gap-2"
      data-testid={testid}
    >
      <div className="w-10 h-10 rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center text-[hsl(var(--primary))]">
        {icon}
      </div>
      <div className="text-sm font-semibold">{label}</div>
    </Link>
  );
}

function ProductRow({ product, onEdit, onDelete, onSell, format }) {
  const totalStock = (product.variants || []).reduce((a, v) => a + (v.stock || 0), 0);
  const isOut = totalStock === 0;
  const isLow = !isOut && totalStock <= 2;

  return (
    <div className="bg-white rounded-2xl lb-shadow-card p-4 lb-fade-in" data-testid={`admin-product-${product.id}`}>
      <div className="flex gap-4">
        <div className="w-16 h-16 shrink-0 bg-secondary rounded-xl overflow-hidden flex items-center justify-center">
          {product.image_base64 ? (
            <img src={product.image_base64} alt="" className="w-full h-full object-cover" />
          ) : (
            <Package className="w-6 h-6 text-muted-foreground/60" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="lb-overline truncate">{product.category}{product.subcategory ? ` · ${product.subcategory}` : ""}</div>
              <h3 className="font-display text-lg leading-tight truncate">{product.name}</h3>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid={`row-menu-${product.id}`}><MoreVertical className="w-4 h-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onEdit} data-testid={`edit-product-${product.id}`}><Pencil className="w-4 h-4 mr-2" /> Editar</DropdownMenuItem>
                <DropdownMenuItem onClick={onDelete} className="text-[hsl(var(--destructive))]" data-testid={`delete-product-${product.id}`}><Trash2 className="w-4 h-4 mr-2" /> Eliminar</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {isOut && <Badge tone="danger">Esgotado</Badge>}
            {isLow && <Badge tone="warn">Stock baixo</Badge>}
            <span className="text-xs text-muted-foreground">{totalStock} unidade{totalStock === 1 ? "" : "s"} · {product.variants?.length || 0} variante(s)</span>
          </div>

          <div className="mt-3 space-y-2">
            {(product.variants || []).map((v) => {
              const vOut = v.stock === 0;
              const vLow = !vOut && v.stock <= 2;
              return (
                <div key={v.id} className="flex items-center justify-between gap-2 rounded-lg bg-secondary px-3 py-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{v.name}</div>
                    <div className="text-xs text-muted-foreground">
                      Venda {format(v.sale_price)} · Compra {format(v.purchase_price)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${vOut ? "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]" : vLow ? "bg-[#D4A373]/20 text-[#8a5a2a]" : "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]"}`}>
                      {v.stock} un.
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={vOut}
                      onClick={() => onSell(v)}
                      className="h-8 text-xs"
                      data-testid={`sell-variant-${v.id}`}
                    >
                      Vender
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function Badge({ tone, children }) {
  const map = {
    danger: "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]",
    warn: "bg-[#D4A373]/20 text-[#8a5a2a]",
    ok: "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]",
  };
  return <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ${map[tone] || map.ok}`}>{children}</span>;
}
